// GroupFeed Component - Post feed for a group

import React, { useState } from 'react';
import {
  Heart,
  MessageCircle,
  MapPin,
  User,
  Send,
  Image,
  BarChart3,
  Pin,
  MoreHorizontal,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  createPost,
  likePost,
  unlikePost,
  deletePost,
  pinPost,
  unpinPost,
  votePoll,
} from '../../services/groupsService';
import type { GroupPost, GroupPostType, PollOption } from '../../types/groups';
import { Timestamp } from '@firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

interface GroupFeedProps {
  groupId: string;
  posts: GroupPost[];
  isMember: boolean;
  onViewProfile?: (userId: string) => void;
  onViewPlace?: (placeId: string) => void;
}

export function GroupFeed({
  groupId,
  posts,
  isMember,
  onViewProfile,
  onViewPlace,
}: GroupFeedProps) {
  const { user } = useAuth();
  const [newPostContent, setNewPostContent] = useState('');
  const [posting, setPosting] = useState(false);

  const handleCreatePost = async () => {
    if (!user || !newPostContent.trim()) return;

    setPosting(true);
    try {
      await createPost(
        {
          groupId,
          type: 'text',
          content: newPostContent.trim(),
        },
        user.uid,
        user.displayName || 'Anonym',
        user.photoURL || undefined
      );
      setNewPostContent('');
    } catch (err) {
      console.error('Error creating post:', err);
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (post: GroupPost) => {
    if (!user) return;
    const isLiked = post.likedBy.includes(user.uid);
    if (isLiked) {
      await unlikePost(groupId, post.id, user.uid);
    } else {
      await likePost(groupId, post.id, user.uid);
    }
  };

  return (
    <div className="space-y-4">
      {/* New Post Form */}
      {isMember && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex gap-3">
            {user?.photoURL ? (
              <img
                src={user.photoURL}
                alt=""
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-400" />
              </div>
            )}
            <div className="flex-1">
              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="Was moechtest du teilen?"
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <div className="flex items-center justify-between mt-2">
                <div className="flex gap-2">
                  <button className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors">
                    <Image className="w-5 h-5" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors">
                    <MapPin className="w-5 h-5" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors">
                    <BarChart3 className="w-5 h-5" />
                  </button>
                </div>
                <button
                  onClick={handleCreatePost}
                  disabled={posting || !newPostContent.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {posting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Posten
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Posts List */}
      {posts.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Noch keine Beitraege</h3>
          <p className="text-gray-500">
            {isMember
              ? 'Sei der Erste, der etwas teilt!'
              : 'Tritt der Gruppe bei, um Beitraege zu sehen und zu posten.'}
          </p>
        </div>
      ) : (
        posts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            groupId={groupId}
            currentUserId={user?.uid}
            onLike={() => handleLike(post)}
            onViewProfile={onViewProfile}
            onViewPlace={onViewPlace}
          />
        ))
      )}
    </div>
  );
}

// Post Card Component
interface PostCardProps {
  post: GroupPost;
  groupId: string;
  currentUserId?: string;
  onLike: () => void;
  onViewProfile?: (userId: string) => void;
  onViewPlace?: (placeId: string) => void;
}

function PostCard({
  post,
  groupId,
  currentUserId,
  onLike,
  onViewProfile,
  onViewPlace,
}: PostCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const isLiked = currentUserId && post.likedBy.includes(currentUserId);
  const isAuthor = currentUserId === post.authorId;

  const formatTime = (date: Timestamp | Date) => {
    const d = date instanceof Timestamp ? date.toDate() : date;
    return formatDistanceToNow(d, { locale: de, addSuffix: true });
  };

  const handleDelete = async () => {
    if (confirm('Beitrag wirklich loeschen?')) {
      await deletePost(groupId, post.id);
    }
    setShowMenu(false);
  };

  const handleVote = async (optionId: string) => {
    if (!currentUserId) return;
    await votePoll(groupId, post.id, optionId, currentUserId);
  };

  const getTotalVotes = (options: PollOption[]) => {
    return options.reduce((sum, opt) => sum + opt.votes.length, 0);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Pinned indicator */}
      {post.isPinned && (
        <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100 flex items-center gap-2 text-sm text-indigo-600">
          <Pin className="w-4 h-4" />
          Angepinnt
        </div>
      )}

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {post.authorPhotoUrl ? (
              <img
                src={post.authorPhotoUrl}
                alt={post.authorName}
                onClick={() => onViewProfile?.(post.authorId)}
                className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80"
              />
            ) : (
              <div
                onClick={() => onViewProfile?.(post.authorId)}
                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200"
              >
                <User className="w-5 h-5 text-gray-400" />
              </div>
            )}
            <div>
              <button
                onClick={() => onViewProfile?.(post.authorId)}
                className="font-medium text-gray-900 hover:text-indigo-600"
              >
                {post.authorName}
              </button>
              <p className="text-sm text-gray-500">{formatTime(post.createdAt)}</p>
            </div>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-8 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                {isAuthor && (
                  <button
                    onClick={handleDelete}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Loeschen
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="mt-3">
          <p className="text-gray-800 whitespace-pre-wrap">{post.content}</p>
        </div>

        {/* Images */}
        {post.imageUrls && post.imageUrls.length > 0 && (
          <div className="mt-3 -mx-4">
            <div className={`grid gap-1 ${post.imageUrls.length > 1 ? 'grid-cols-2' : ''}`}>
              {post.imageUrls.slice(0, 4).map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt=""
                  className="w-full object-cover aspect-video"
                />
              ))}
            </div>
          </div>
        )}

        {/* Shared Place */}
        {post.type === 'place_share' && post.sharedPlaceId && (
          <button
            onClick={() => onViewPlace?.(post.sharedPlaceId!)}
            className="mt-3 w-full p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-3"
          >
            {post.sharedPlaceImageUrl && (
              <img
                src={post.sharedPlaceImageUrl}
                alt=""
                className="w-16 h-16 rounded-lg object-cover"
              />
            )}
            <div className="flex-1 text-left">
              <div className="flex items-center gap-1 text-indigo-600">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">{post.sharedPlaceName}</span>
              </div>
            </div>
          </button>
        )}

        {/* Poll */}
        {post.type === 'poll' && post.pollOptions && (
          <div className="mt-3 space-y-2">
            {post.pollOptions.map((option) => {
              const totalVotes = getTotalVotes(post.pollOptions!);
              const percentage = totalVotes > 0 ? (option.votes.length / totalVotes) * 100 : 0;
              const hasVoted = currentUserId && option.votes.includes(currentUserId);

              return (
                <button
                  key={option.id}
                  onClick={() => handleVote(option.id)}
                  className={`w-full p-3 rounded-lg border relative overflow-hidden transition-colors ${
                    hasVoted
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div
                    className="absolute inset-0 bg-indigo-100 transition-all"
                    style={{ width: `${percentage}%` }}
                  />
                  <div className="relative flex items-center justify-between">
                    <span className={hasVoted ? 'font-medium text-indigo-700' : 'text-gray-700'}>
                      {option.text}
                    </span>
                    <span className="text-sm text-gray-500">
                      {option.votes.length} ({percentage.toFixed(0)}%)
                    </span>
                  </div>
                </button>
              );
            })}
            <p className="text-sm text-gray-500 text-center">
              {getTotalVotes(post.pollOptions)} Stimmen
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
          <button
            onClick={onLike}
            className={`flex items-center gap-1.5 text-sm transition-colors ${
              isLiked
                ? 'text-red-500'
                : 'text-gray-500 hover:text-red-500'
            }`}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
            {post.likeCount > 0 && <span>{post.likeCount}</span>}
          </button>

          <button className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-indigo-500 transition-colors">
            <MessageCircle className="w-5 h-5" />
            {post.commentCount > 0 && <span>{post.commentCount}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GroupFeed;
